import React, { useState } from 'react';
import { CreditCard, DollarSign, Shield, CheckCircle, RefreshCcw } from 'lucide-react';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import { useGroupFeatures } from '../hooks/useGroupFeatures.ts';
import { usePaybillBalance } from '../hooks/usePaybillBalance.ts';
import DisbursementModal from '../components/DisbursementModal.tsx';
import DisbursementOtpModal from '../components/DisbursementOtpModal.tsx';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Table from '../components/Table.tsx';
import { PermissionPage, PermissionGate } from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';

export default function DisbursementPage() {
  const { can } = usePermissions();
  const isAdmin = useIsAdmin();
  const { isFeatureSelected } = useGroupFeatures();
  const queryClient = useQueryClient();
  const {
    balance,
    currency,
    message,
    isLoading,
    isFetching,
    error,
    refetch,
    success
  } = usePaybillBalance();

  // Modal state
  const [modal, setModal] = useState({ type: null, data: null }); // type: 'manual' | 'loan' | null
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpDisbursementId, setOtpDisbursementId] = useState(null);
  const [otpExpiresAt, setOtpExpiresAt] = useState(null);
  const [otpSuccess, setOtpSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('disbursements'); // 'disbursements' | 'loans'
  const [disbursementPage, setDisbursementPage] = useState(1);
  const [loanToDisburse, setLoanToDisburse] = useState(null);

  // Check if money disbursement feature is enabled
  const isDisbursementEnabled = isFeatureSelected('money_disbursement');

  // Handle OTP modal open after disbursement
  const handleOtpRequired = (disbursementId, expiresAt) => {
    setOtpDisbursementId(disbursementId);
    setOtpExpiresAt(expiresAt);
    setShowOtpModal(true);
  };

  // Handle OTP success
  const handleOtpSuccess = () => {
    setOtpSuccess(true);
    setTimeout(() => setOtpSuccess(false), 2000);
    // Refresh approved loans to fetch latest data
    queryClient.invalidateQueries({ queryKey: ['approved-loans'] });
  };

  // Fetch recent disbursements (paginated)
  const { data: disbursementData, isLoading: isLoadingDisbursements, error: disbursementError } = useQuery({
    queryKey: ['recent-disbursements', disbursementPage],
    queryFn: async () => {
      const res = await import('../utils/axios.ts').then(m => m.default.get(`/disbursements?page=${disbursementPage}`));
      return res.data;
    },
    staleTime: 60 * 1000,
  });
  const disbursements = disbursementData?.data?.data || [];
  const disbursementMeta = disbursementData?.data?.meta || {};

  // Fetch approved-but-not-disbursed loans
  const { data: loansData, isLoading: isLoadingLoans, error: loansError } = useQuery({
    queryKey: ['approved-loans'],
    queryFn: async () => {
      const res = await import('../utils/axios.ts').then(m => m.default.get('/memberloan', { params: { status: 'APPROVED' } }));
      return res.data.data;
    },
    staleTime: 60 * 1000,
  });
  const approvedLoans = loansData || [];

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">Only administrators can access disbursement features.</p>
        </div>
      </div>
    );
  }

  if (!isDisbursementEnabled) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <CreditCard className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Feature Not Enabled</h1>
          <p className="text-gray-600 mb-4">Money disbursement feature is not enabled for your group.</p>
          <p className="text-sm text-gray-500">Please enable this feature in Settings &gt; Features to access disbursement functionality.</p>
        </div>
      </div>
    );
  }

  return (
    <PermissionPage permission={PERMISSIONS.VIEW_DISBURSEMENTS}>
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <CreditCard className="w-8 h-8 text-blue-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Disbursement</h1>
          </div>
          <p className="text-gray-600">
            Securely disburse money from your M-Pesa paybill to recipients.
          </p>
        </div>

        {/* Actions Row */}
        <div className="flex items-center gap-4 mb-6">
          <PermissionGate permission={PERMISSIONS.CREATE_DISBURSEMENT}>
          <button
            className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 flex items-center"
            onClick={() => setModal({ type: 'manual', data: null })}
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Manual Disbursement
          </button>
          </PermissionGate>
        </div>

        {/* Tabs */}
        <div className="mb-4 flex gap-2 border-b">
          <button
            className={`px-4 py-2 font-medium border-b-2 ${activeTab === 'disbursements' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500'}`}
            onClick={() => setActiveTab('disbursements')}
          >
            Recent Disbursements
          </button>
          <button
            className={`px-4 py-2 font-medium border-b-2 ${activeTab === 'loans' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500'}`}
            onClick={() => setActiveTab('loans')}
          >
            Approved Loans (Not Disbursed)
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'disbursements' ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Disbursements</h2>
            {disbursementError && (
              <div className="mb-4 text-red-700 bg-red-50 border border-red-200 rounded p-2">
                Error loading disbursements: {disbursementError.message}
              </div>
            )}
            {otpSuccess && (
              <div className="mb-4 text-green-700 bg-green-50 border border-green-200 rounded p-2 text-center">
                Disbursement verified and will be processed.
              </div>
            )}
            <Table
              columns={[
                {
                  label: 'Member',
                  field: 'recipient_name',
                  render: row => row.recipient_name,
                },
                {
                  label: 'Amount',
                  field: 'amount',
                  render: row => `${row.currency} ${Number(row.amount).toLocaleString()}`,
                },
                {
                  label: 'Type',
                  field: 'type',
                  render: row => row.loan_id ? 'Loan' : 'Manual',
                },
                {
                  label: 'Status',
                  field: 'status',
                  render: row => (
                    <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                      row.status === 'completed' ? 'bg-green-100 text-green-700' :
                      row.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      row.status === 'failed' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {row.status}
                    </span>
                  ),
                },
                {
                  label: 'Date',
                  field: 'created_at',
                  render: row => new Date(row.created_at).toLocaleString(),
                },
                {
                  label: 'Notes',
                  field: 'notes',
                  render: row => row.notes || '—',
                },
              ]}
              data={disbursements}
              page={disbursementPage}
              perPage={disbursementMeta.per_page || 10}
              total={disbursementMeta.total || 0}
              onPageChange={setDisbursementPage}
              loading={isLoadingDisbursements}
            />
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Approved Loans (Not Disbursed)</h2>
            {loansError && (
              <div className="mb-4 text-red-700 bg-red-50 border border-red-200 rounded p-2">
                Error loading loans: {loansError.message}
              </div>
            )}
            {isLoadingLoans ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : approvedLoans.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No approved loans pending disbursement.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="responsive-table min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-3 py-2 text-left">Loan No</th>
                      <th className="px-3 py-2 text-left">Member</th>
                      <th className="px-3 py-2 text-left">Principal</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvedLoans.map(loan => (
                      <tr key={loan.id} className="border-b last:border-0">
                        <td data-label="Loan No" className="px-3 py-2 font-mono text-sm">{loan.loan_no || `#${loan.id}`}</td>
                        <td data-label="Member" className="px-3 py-2">{loan.member?.name || loan.member_name}</td>
                        <td data-label="Principal" className="px-3 py-2">KES {Number(loan.principal).toLocaleString()}</td>
                        <td data-label="Status" className="px-3 py-2 capitalize">
                          <span className="inline-block px-2 py-1 rounded text-xs font-semibold bg-yellow-100 text-yellow-700">{loan.status}</span>
                        </td>
                        <td data-label="Action" className="px-3 py-2">
                          <button
                            className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                            onClick={() => setLoanToDisburse(loan)}
                          >
                            Disburse
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Modals */}
      <DisbursementModal
        isOpen={!!modal.type}
        onClose={() => setModal({ type: null, data: null })}
        onOtpRequired={handleOtpRequired}
      />
      <DisbursementOtpModal
        isOpen={showOtpModal}
        onClose={() => setShowOtpModal(false)}
        disbursementId={otpDisbursementId}
        otpExpiresAt={otpExpiresAt}
        onSuccess={handleOtpSuccess}
      />
      {/* Loan Disbursement Modal */}
      {loanToDisburse && (
        <DisbursementModal
          isOpen={!!loanToDisburse}
          onClose={() => setLoanToDisburse(null)}
          onOtpRequired={handleOtpRequired}
          loanData={{
            recipient_name: loanToDisburse.member?.name || loanToDisburse.member_name,
            recipient_phone: loanToDisburse.member?.phone_number || loanToDisburse.member_phone,
            principal: loanToDisburse.principal,
            loan_id: loanToDisburse.id,
            member_id: loanToDisburse.member_id,
          }}
        />
      )}
    </div>
    </PermissionPage>
  );
} 